/**
 * Canonical ID / SKU derivation. ONE deterministic rule set used by the SPA,
 * Worker, Shopify sync scripts, and (mirrored in Python) the bake agent.
 *
 * sizeId:  "ct-4x6" | "ct-3.5x5" | "fa-16x20" | "fa-A3"
 * frameId: "fr-{CODE}-{printSize}[-{COLOUR}]"  e.g. "fr-BM-8x10" (black, the
 *          unsuffixed default) | "fr-BM-8x10-wh" | "fr-ICRS-16x24-oak"
 * SKU:
 *   bare C-Type:     "RPLP-CT-4X6"        (finish is a line property, not a variant)
 *   C-Type bulk:     "RPLP-CT-4X6-B"      (dedicated bulk-tier variant priced at bulkPrice —
 *                                          non-Plus stores can't run Function discounts, so the
 *                                          engine swaps the group to this SKU at the threshold)
 *   bare fine art:   "RPLP-FA-16X20-STD" | "RPLP-FA-A3-FART"   (tier suffix)
 *   specialty:       "RPLP-FA-A1-BIZMED" | "RPLP-FA-A1-BIZTHK"
 *   framed (coupled):"RPLP-FR-BM-8X10"   (print INCLUDED; paper code + print size)
 *                    "RPLP-FR-BM-8X10-WH" (non-default frame colour appends its code)
 *   urgency fee:     "RPLP-FEE-URG-3540" (cents-encoded urgent-processing surcharge)
 *
 * SKU size fragment: uppercase, strip "." ("3.5x5" -> "35X5", "5x7.5" -> "5X75", "A3" -> "A3").
 */

import type { BorderStyle, PaperId, PaperTier, ProductFamily } from './types';

export const SKU_PREFIX = 'RPLP-';

/** Frame paper code map — stable, mirrors pricing.framing.paperCodes. */
export const FRAME_PAPER_CODES: Record<PaperId, string | null> = {
  gloss: 'GL',
  lustre: 'LU',
  'bauhaus-matt': 'BM',
  'bauhaus-lustre': 'BL',
  'bauhaus-metallic': 'MET',
  'ilford-pearl': 'IGP',
  'ilford-cottonrag-smooth': 'ICRS',
  'ilford-cottonrag-textured': 'ICRT',
  'bizan-medium': null,
  'bizan-thick': null,
};

const CODE_TO_PAPER: Record<string, PaperId> = Object.fromEntries(
  Object.entries(FRAME_PAPER_CODES)
    .filter(([, code]) => code)
    .map(([paper, code]) => [code as string, paper as PaperId]),
);

/** Specialty paper SKU codes. */
export const SPECIALTY_CODES: Partial<Record<PaperId, string>> = {
  'bizan-medium': 'BIZMED',
  'bizan-thick': 'BIZTHK',
};

/** "4x6" -> "4X6", "3.5x5" -> "35X5", "A3" -> "A3" */
export function sizeToSkuFragment(size: string): string {
  return size.replace(/\./g, '').toUpperCase();
}

export function ctypeSizeId(size: string): string {
  return `ct-${size}`;
}

export function fineArtSizeId(size: string): string {
  return `fa-${size}`;
}

/** Is this a specialty (explicit per-paper price) paper? */
export function isSpecialtyPaper(paper: PaperId): boolean {
  return paper in SPECIALTY_CODES;
}

/**
 * `colourCode` is the colour's skuCode. Black passes '' (or nothing) and the id
 * comes out exactly as it did before colours existed — that is what makes this
 * a zero-migration change for every persisted frame_id and past order.
 */
export function frameId(paper: PaperId, printSize: string, colourCode = ''): string {
  const code = FRAME_PAPER_CODES[paper];
  if (!code) throw new Error(`Paper ${paper} cannot be framed`);
  const suffix = colourCode ? `-${colourCode.toLowerCase()}` : '';
  return `fr-${code}-${printSize}${suffix}`;
}

/** Fine-art SKU tier code from a paper tier. */
export function tierCode(tier: PaperTier): 'STD' | 'FART' {
  return tier === 'fineArt' ? 'FART' : 'STD';
}

/** SKU for a C-Type size (finish does NOT change the SKU — tiers sum across finishes). */
export function ctypeSku(size: string): string {
  return `${SKU_PREFIX}CT-${sizeToSkuFragment(size)}`;
}

/**
 * SKU for a C-Type size's dedicated BULK variant, e.g. "RPLP-CT-4X6-B".
 * The variant's Shopify price IS the bulkPrice: on non-Plus stores the bulk
 * tier can't run as a Function discount, so computeTotals moves an active
 * group onto this SKU instead (variant price == charged price).
 */
export function ctypeBulkSku(size: string): string {
  return `${ctypeSku(size)}-B`;
}

/** SKU for a fine-art size + paper price tier (standard|fineArt). */
export function fineArtSku(size: string, tier: PaperTier): string {
  return `${SKU_PREFIX}FA-${sizeToSkuFragment(size)}-${tierCode(tier)}`;
}

/** SKU for a specialty (paper-specific) fine-art print. */
export function specialtySku(size: string, paper: PaperId): string {
  const code = SPECIALTY_CODES[paper];
  if (!code) throw new Error(`Paper ${paper} is not a specialty paper`);
  return `${SKU_PREFIX}FA-${sizeToSkuFragment(size)}-${code}`;
}

/** SKU for a framed product (print + frame, one variant). Black omits the suffix. */
export function frameSku(paper: PaperId, printSize: string, colourCode = ''): string {
  const code = FRAME_PAPER_CODES[paper];
  if (!code) throw new Error(`Paper ${paper} cannot be framed`);
  const suffix = colourCode ? `-${colourCode.toUpperCase()}` : '';
  return `${SKU_PREFIX}FR-${code}-${sizeToSkuFragment(printSize)}${suffix}`;
}

export interface ParsedSizeId {
  family: 'ctype' | 'fine_art';
  size: string;
  /** true for customer-defined custom sizes (cu- prefix; fine-art family). */
  custom?: boolean;
}

/** Parse a bare-print sizeId back to its parts. Throws on malformed input. */
export function parseSizeId(id: string): ParsedSizeId {
  if (id.startsWith('ct-')) return { family: 'ctype', size: id.slice(3) };
  if (id.startsWith('fa-')) return { family: 'fine_art', size: id.slice(3) };
  if (id.startsWith('cu-')) return { family: 'fine_art', size: id.slice(3), custom: true };
  throw new Error(`Unknown sizeId family: ${id}`);
}

/** Custom sizeId from inches (0.1in precision), e.g. cu-11.5x17. Long edge second. */
export function customSizeId(wIn: number, hIn: number): string {
  const r = (n: number) => (Math.round(n * 10) / 10).toString();
  const [a, b] = wIn <= hIn ? [wIn, hIn] : [hIn, wIn];
  return `cu-${r(a)}x${r(b)}`;
}

/** Inches from a custom sizeId. Throws if not a cu- id. */
export function customSizeInches(id: string): [number, number] {
  const p = parseSizeId(id);
  if (!p.custom) throw new Error(`Not a custom sizeId: ${id}`);
  const [a, b] = p.size.split('x').map(Number);
  if (!(a > 0) || !(b > 0)) throw new Error(`Malformed custom sizeId: ${id}`);
  return [a, b];
}

/** SKU for a custom print at a whole-dollar price: RPLP-CU-STD-87 / RPLP-CU-FART-87. */
export function customSku(tier: PaperTier, dollars: number): string {
  if (!Number.isInteger(dollars) || dollars <= 0) throw new Error(`Custom SKU needs whole dollars: ${dollars}`);
  return `${SKU_PREFIX}CU-${tierCode(tier)}-${dollars}`;
}

/** Urgency-fee SKU shape: RPLP-FEE-URG-{cents}. Capture group 1 = cents. */
export const URGENCY_FEE_SKU_RE = /^RPLP-FEE-URG-(\d+)$/;

/** SKU for the urgent-processing fee at a whole-cents price: RPLP-FEE-URG-3540. */
export function urgencyFeeSku(cents: number): string {
  if (!Number.isInteger(cents) || cents <= 0) throw new Error(`Urgency fee SKU needs whole cents: ${cents}`);
  return `${SKU_PREFIX}FEE-URG-${cents}`;
}

/** Cents encoded in an urgency-fee SKU, or null when it is not one. */
export function parseUrgencyFeeSku(sku: string): number | null {
  const m = URGENCY_FEE_SKU_RE.exec(sku);
  return m ? Number(m[1]) : null;
}

export interface ParsedFrameId {
  paper: PaperId;
  printSize: string;
  /** Colour skuCode, lowercased. '' = the unsuffixed default (black). */
  colourCode: string;
}

/**
 * Parse a frameId back to paper + printSize + colour.
 *
 *   "fr-BM-8x10"     -> { paper: 'bauhaus-matt', printSize: '8x10', colourCode: '' }
 *   "fr-BM-8x10-wh"  -> { ..., colourCode: 'wh' }
 *
 * A THIRD dash is unambiguously the colour: no print size in the catalogue
 * contains a dash (they are 8x10, 3.5x5, A3 and so on), so anything trailing
 * the size is a colour code. An id with no colour segment is black, which is
 * what every id minted before 2026-07-28 is.
 */
export function parseFrameId(id: string): ParsedFrameId {
  if (!id.startsWith('fr-')) throw new Error(`Not a frameId: ${id}`);
  const rest = id.slice(3);
  const dash = rest.indexOf('-');
  if (dash === -1) throw new Error(`Malformed frameId: ${id}`);
  const code = rest.slice(0, dash);
  let printSize = rest.slice(dash + 1);
  let colourCode = '';
  const tail = printSize.lastIndexOf('-');
  if (tail !== -1) {
    colourCode = printSize.slice(tail + 1).toLowerCase();
    printSize = printSize.slice(0, tail);
  }
  const paper = CODE_TO_PAPER[code];
  if (!paper) throw new Error(`Unknown frame paper code in: ${id}`);
  if (!printSize) throw new Error(`Malformed frameId: ${id}`);
  return { paper, printSize, colourCode };
}

/**
 * SKU for a bare-print sizeId + paper. Specialty papers → specialty SKU;
 * fine-art tier papers → tiered SKU; C-Type → C-Type SKU.
 * (For framed items use frameSku()/the frameId directly.)
 */
export function skuFor(sizeId: string, paper: PaperId, paperTier: PaperTier): string {
  const parsed = parseSizeId(sizeId);
  if (parsed.family === 'ctype') return ctypeSku(parsed.size);
  if (isSpecialtyPaper(paper)) return specialtySku(parsed.size, paper);
  if (paperTier === 'ctype') throw new Error(`Fine art needs a fine-art paper tier: ${sizeId}`);
  return fineArtSku(parsed.size, paperTier);
}

/** Human display label: "4 × 6\"" · "5 × 7.5\"" · "A3 (11.7 × 16.5\")" */
export function sizeLabel(size: string, inches: [number, number]): string {
  if (/^A\d$/.test(size)) {
    return `${size} (${inches[0]} × ${inches[1]}")`;
  }
  const [a, b] = size.split('x');
  return `${a} × ${b}"`;
}

/** Grouping key for cart lines / bake folders. Framed items key by frameId. */
export function groupKey(sizeId: string, paper: PaperId | null, frameId?: string): string {
  if (frameId) return frameId;
  return paper ? `${sizeId}|${paper}` : sizeId;
}

/** SKU family sniffing for webhook-side detection (mirrored in Python). */
export function skuFamily(sku: string): ProductFamily | null {
  if (!sku.startsWith(SKU_PREFIX)) return null;
  const body = sku.slice(SKU_PREFIX.length);
  // Prefix match covers the "-B" bulk-variant suffix too (RPLP-CT-4X6-B → 'ctype').
  if (body.startsWith('CT-')) return 'ctype';
  if (body.startsWith('FA-')) return 'fine_art';
  if (body.startsWith('CU-')) return 'fine_art';
  if (body.startsWith('FR-')) return 'frame';
  return null;
}

export type { BorderStyle };
